import React, { useState } from 'react'
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import SearchIcon from '@mui/icons-material/Search'
import CancelIcon from '@mui/icons-material/Cancel'
import { DesktopDatePicker } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import ja from 'date-fns/locale/ja'
import { Category, StatusType, depositStatusType } from '~/src/constants/requestOrder'
import { useLocation } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { HeaderSearchTextCancelButton, HeaderSearchTextField } from '~/src/components/molecules'
import moment from 'moment'

interface IContactOrderFormProps {
  onSearch: (values: {
    orderedAtBegin: string
    orderedAtEnd: string
    keyword: string
    status: string
  }) => void
}
export const ContactOrderForm: React.FC<IContactOrderFormProps> = ({ onSearch }): JSX.Element => {
  const { t } = useTranslation()
  const location: Location = useLocation()
  const [formValues, setFormValues] = useState<{
    orderedAtBegin: string
    orderedAtEnd: string
    keyword: string
    status: string
    companyName: string
    categoryName: string
    estimateAtBegin: string
    estimateAtEnd: string
    name: string
    carName: string
    modelCode: string
    engineModel: string
    isDeposited: string
    updatedAtBegin: string
    updatedAtEnd: string
  }>({
    // @ts-ignore
    orderedAtBegin: location.state?.orderedAtBegin || '',
    // @ts-ignore
    orderedAtEnd: location.state?.orderedAtEnd || '',
    // @ts-ignore
    keyword: location.state?.keyword || '',
    // @ts-ignore
    status: location.state?.status || '',
    // @ts-ignore
    companyName: location.state?.companyName || '',
    // @ts-ignore
    categoryName: location.state?.categoryName || '',
    // @ts-ignore
    estimateAtBegin: location.state?.estimateAtBegin || '',
    // @ts-ignore
    estimateAtEnd: location.state?.estimateAtEnd || '',
    // @ts-ignore
    name: location.state?.name || '',
    // @ts-ignore
    carName: location.state?.carName || '',
    // @ts-ignore
    modelCode: location.state?.modelCode || '',
    // @ts-ignore
    engineModel: location.state?.engineModel || '',
    // @ts-ignore
    isDeposited: location.state?.isDeposited || '',
    // @ts-ignore
    updatedAtBegin: location.state?.updatedAtBegin || '',
    // @ts-ignore
    updatedAtEnd: location.state?.updatedAtEnd || '',
  })

  const handleChange = (e: Event) => {
    if (formValues.hasOwnProperty(e.target.name)) {
      setFormValues({ ...formValues, [e.target.name]: e.target.value })
    }
  }

  const handleClear = (key: string) => {
    setFormValues({ ...formValues, [key]: '' })
  }

  const handleClears = () => {
    onSearch({
      orderedAtBegin: '',
      orderedAtEnd: '',
      keyword: '',
      status: '',
      companyName: '',
      categoryName: '',
      estimateAtBegin: '',
      estimateAtEnd: '',
      name: '',
      carName: '',
      modelCode: '',
      engineModel: '',
      isDeposited: '',
      updatedAtBegin: '',
      updatedAtEnd: '',
    })
    setFormValues({
      orderedAtBegin: '',
      orderedAtEnd: '',
      keyword: '',
      status: '',
      companyName: '',
      categoryName: '',
      estimateAtBegin: '',
      estimateAtEnd: '',
      name: '',
      carName: '',
      modelCode: '',
      engineModel: '',
      isDeposited: '',
      updatedAtBegin: '',
      updatedAtEnd: '',
    });
  };

  const handleClearKeyword = () => {
    setFormValues({
      ...formValues,
      keyword: '',
    })
  }

  const getKeyByValue = (object: Record<string, unknown>, value: unknown) => {
    return Object.keys(object).find((key) => object[key] === value)
  }

  return (
    <Grid container sx={{ width: "1800px", marginBottom: "24px", alignItems: "flex-end" }}>
      <Grid item lg={6}>
        <Grid container spacing={1}>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="keyword"
              value={formValues.keyword}
              onChange={handleChange}
              placeholder={t('contact_order.keyword')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('keyword')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <Select
              name="status"
              value={formValues.status}
              displayEmpty
              renderValue={(value) =>
                getKeyByValue(StatusType, Number(value)) || t('contact_order.status')
              }
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={handleChange}
              sx={{
                color: !formValues.status ? 'rgba(0,0,0,0.5)' : '1',
                width: '220px',
                fontSize: '16px',
                lineHeight: '24px',
                fontStyle: 'normal',
                height: '40px',
                background: 'white',
                marginTop: 0,
              }}
            >
              <MenuItem value={''}></MenuItem>
              <MenuItem value={'100'}>PENDING</MenuItem>
              <MenuItem value={'300'}>NEGOTIATION</MenuItem>
              <MenuItem value={'400'}>CANCEL</MenuItem>
              <MenuItem value={'500'}>ACCEPTED</MenuItem>
              <MenuItem value={'600'}>IN PROGRESS</MenuItem>
              <MenuItem value={'1000'}>CLOSED</MenuItem>
            </Select>
          </Grid>
          <Grid item lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <DesktopDatePicker
                name="updatedAtBegin"
                value={formValues.updatedAtBegin || null}
                onChange={(newValue) =>
                  setFormValues({ ...formValues, updatedAtBegin: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })
                }
                inputFormat="yyyy/MM/dd"
                mask="____/__/__"
                renderInput={(params) => (
                  <Box
                    sx={{
                      width: '220px',
                      height: '40px',
                    }}
                  >
                    <TextField
                      {...params}
                      size={'small'}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: t('contact_order.updated_start_date'),
                        style: { fontSize: 16 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          height: 'auto',
                        },
                        fontStyle: 'normal',
                        lineHeight: '24px',
                        letterSpacing: 0.15,
                        background: 'white',
                      }}
                    />
                  </Box>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                name="updatedAtEnd"
                value={formValues.updatedAtEnd || null}
                onChange={(newValue) => 
                  setFormValues({ ...formValues, updatedAtEnd: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })
                }
                inputFormat="yyyy/MM/dd"
                mask="____/__/__"
                renderInput={(params) => (
                  <Box
                    sx={{
                      width: '220px',
                      height: '40px',
                    }}
                  >
                    <TextField
                      {...params}
                      size={'small'}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: `${t('contact_order.updated_end_date')}`,
                        style: { fontSize: 16 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          height: 'auto',
                        },
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: 0.15,
                        background: 'white',
                      }}
                    />
                  </Box>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="companyName"
              value={formValues.companyName}
              onChange={handleChange}
              placeholder={t('contact_order.company_name')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('companyName')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <Select
              name="categoryName"
              value={formValues.categoryName}
              displayEmpty
              renderValue={(value) =>
                getKeyByValue(Category, value) || t('contact_order.category_name')
              }
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={handleChange}
              sx={{
                color: !formValues.categoryName ? 'rgba(0,0,0,0.5)' : '1',
                width: '220px',
                fontSize: '16px',
                lineHeight: '24px',
                fontStyle: 'normal',
                height: '40px',
                background: 'white',
                marginTop: 0,
              }}
            >
              <MenuItem value={''}></MenuItem>
              <MenuItem value={'CKD'}>CKD</MenuItem>
              <MenuItem value={'Part'}>Part</MenuItem>
              <MenuItem value={'Engine'}>Engine</MenuItem>
            </Select>
          </Grid>
          <Grid item lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <DesktopDatePicker
                name="orderedAtBegin"
                value={formValues.orderedAtBegin || null}
                onChange={(newValue) =>
                  setFormValues({ ...formValues, orderedAtBegin: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })
                }
                inputFormat="yyyy/MM/dd"
                mask="____/__/__"
                renderInput={(params) => (
                  <Box
                    sx={{
                      width: '220px',
                      height: '40px',
                    }}
                  >
                    <TextField
                      {...params}
                      size={'small'}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: t('contact_order.ordered_start_date'),
                        style: { fontSize: 16 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          height: 'auto',
                        },
                        fontStyle: 'normal',
                        lineHeight: '24px',
                        letterSpacing: 0.15,
                        background: 'white',
                      }}
                    />
                  </Box>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                name="orderedAtEnd"
                value={formValues.orderedAtEnd || null}
                onChange={(newValue) => setFormValues({ ...formValues, orderedAtEnd: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })}
                inputFormat="yyyy/MM/dd"
                mask="____/__/__"
                renderInput={(params) => (
                  <Box
                    sx={{
                      width: '220px',
                      height: '40px',
                    }}
                  >
                    <TextField
                      {...params}
                      size={'small'}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: `${t('contact_order.ordered_end_date')}`,
                        style: { fontSize: 16 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          height: 'auto',
                        },
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: 0.15,
                        background: 'white',
                      }}
                    />
                  </Box>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="name"
              value={formValues.name}
              onChange={handleChange}
              placeholder={t('contact_order.product_name')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('name')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="carName"
              value={formValues.carName}
              onChange={handleChange}
              placeholder={t('contact_order.car_name')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('carName')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="modelCode"
              value={formValues.modelCode}
              onChange={handleChange}
              placeholder={t('contact_order.vehicle_model')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('modelCode')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <HeaderSearchTextField
              name="engineModel"
              value={formValues.engineModel}
              onChange={handleChange}
              placeholder={t('contact_order.engine_model')}
              InputProps={{
                endAdornment:
                  <HeaderSearchTextCancelButton
                    onClick={() => handleClear('engineModel')}
                  />
              }}
            />
          </Grid>
          <Grid item lg={3}>
            <Select
              name="isDeposited"
              value={formValues.isDeposited}
              displayEmpty
              renderValue={(value) =>
                getKeyByValue(depositStatusType, value) || t('contact_order.deposit_status')
              }
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={handleChange}
              sx={{
                color: !formValues.isDeposited? 'rgba(0,0,0,0.5)' : '1',
                width: '220px',
                fontSize: '16px',
                lineHeight: '24px',
                fontStyle: 'normal',
                height: '40px',
                background: 'white',
                marginTop: 0,
              }}
            >
              <MenuItem value={''}></MenuItem>
              <MenuItem value={'false'}>{t('contact_order.deposit_payment_yet')}</MenuItem>
              <MenuItem value={'true'}>{t('contact_order.deposit_payment_done')}</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Grid>
    
      <Grid item lg={6}>
        <Box display={'flex'} alignItems={'center'}>
          <Button
            variant="outlined"
            sx={{
              ml: '8px',
              height: '40px',
              padding: '7px 22px',
              background: '#FFFFFF',
            }}
            onClick={handleClears}
          >
            <Typography
              sx={{
                fontStyle: 'normal',
                fontSize: 15,
                letterSpacing: 0.46,
                lineHeight: '26px',
                fontWeight: 500,
              }}
            >
              {t('contact_order.clear')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            sx={{
              ml: '8px',
              height: '40px',
              padding: '7px 22px 7px 19px',
            }}
            onClick={() => onSearch(formValues)}
          >
            <SearchIcon fontSize={'small'} sx={{ width: '20px', height: '20px' }} />
            <Typography
              sx={{
                marginLeft: '3px',
                fontStyle: 'normal',
                fontSize: 15,
                letterSpacing: 0.46,
                lineHeight: '26px',
                fontWeight: 500,
              }}
            >
              {t('contact_order.search')}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}
